import * as ACTIONS from '@/store/constants/actions.js';
import * as GETTERS from '@/store/constants/getters.js';
import * as MUTATIONS from '@/store/constants/mutations.js';
import { getQuickSearchResults } from '@/generated/console.js';
import { fetchCurrentUser, fetchUserByUuidWithoutSite } from '@/resources/user.js';
import {
    parseV2, parseV3, mergeBanHistories, banHistoryPayload,
} from '@/store/users.js';
import { IS_OPERATOR } from '@/constants/filters.js';
import { ZERO_UUID } from 'commons/constants/parse.js';
import { isSociallyIngestedContent } from '@/util/social.js';
import { getDefaultUser } from './state.js';
import { VIAFOURA_ROOT_UUID } from 'commons/constants/parse.js';
import { fetchCurrentUserSections, fetchUserByUuidAndSection, fetchUserBanHistory } from '../../../resources/iam';
import { sectionLineage } from '@/store/helpers/sections.js';
import { logInfo } from '@/util/logger.js';
import { classifyUserLoadFailure, isTransientUserLoadFailure } from '@/store/helpers/user_load.js';
import { USER_LOAD_STATUSES } from '@/constants/users.js';

const RECENT_CONTENT_PAGE_SIZE = 3;
const RECENT_CONTENT_SORT_ORDER = 'desc';
const RECENT_CONTENT_SORT_FIELD = 'date_created';

// A moderator legitimately has no scope on some ancestor sections, so 403/404 there is expected and must
// only drop that section's bans. Anything else is an unexpected failure worth a breadcrumb — it still must
// not break the user fetch, but it should not vanish silently either.
const EXPECTED_BAN_HISTORY_STATUSES = [ 403, 404 ];

const onBanHistoryError = (error, uuid, sectionUuid) => {
    const status = ((error || {}).response || {}).status;
    if (!EXPECTED_BAN_HISTORY_STATUSES.includes(status)) {
        logInfo(error, 'ban_history_fetch_failed', [
            {
                key: 'user_uuid',
                value: uuid,
            },
            {
                key: 'section_uuid',
                value: sectionUuid,
            },
            {
                key: 'status',
                value: status,
            },
        ]);
    }
    // null marks a failed read so it stays distinguishable from a section that genuinely has no bans.
    return null;
};

// Read ban history for the section under moderation and each of its ancestors: an umbrella ban recorded
// on a parent section still applies here, while a sibling site's ban does not. Each section resolves
// independently, and a failure never rejects — see mergeBanHistories for what happens when they all fail.
const fetchBanHistory = (uuid, sectionUuids) =>
    Promise.all(sectionUuids.map((sectionUuid) =>
        fetchUserBanHistory(uuid, sectionUuid)
            .then(banHistoryPayload)
            .catch((error) => onBanHistoryError(error, uuid, sectionUuid))))
        .then(mergeBanHistories);

export const actions = {
    async [ACTIONS.FETCH_CURRENT_USER]({ getters, commit }) {
        const active_site_uuid = getters[GETTERS.ACTIVE_SITE_UUID];

        try {
            const currentUser = await fetchCurrentUser(active_site_uuid);
            const currentUserSections = await fetchCurrentUserSections();
            if (currentUserSections.roles.order[0] === VIAFOURA_ROOT_UUID && currentUserSections.roles.sections[VIAFOURA_ROOT_UUID].administrator) {
                currentUser.user_privilege = 'global_administrator';
            }
            commit(MUTATIONS.SET_CURRENT_USER, currentUser);
            return currentUser;
        }
        catch (e) {
            return Promise.reject(e);
        }
    },

    [ACTIONS.REGISTER_USER]({ commit }, { user, isV2 = true }) {
        const parsed = isV2 ? parseV2(user) : parseV3(user);
        commit(MUTATIONS.SET_USER, {
            uuid: parsed.uuid,
            user: {
                ...parsed,
                // Clears a previous failure so a successful retry stops rendering the error state.
                load_status: USER_LOAD_STATUSES.LOADED,
            },
        });
        return Promise.resolve(parsed);
    },

    [ACTIONS.FETCH_USERS_RECENT_CONTENT]({ commit, dispatch }, { user_uuid }) {
        const params = {
            searchDefinition: {
                filters: [ {
                    field: 'actor_uuid',
                    operator: IS_OPERATOR,
                    values: [ user_uuid ],
                } ],
            },
            page_size: RECENT_CONTENT_PAGE_SIZE,
            sort_field: RECENT_CONTENT_SORT_FIELD,
            sort_order: RECENT_CONTENT_SORT_ORDER,
        };
        return getQuickSearchResults(params).then((response) => {
            Promise.all(response.content.map((item) => {
                delete item.tags; // Don't replace the tags!
                return dispatch(ACTIONS.SET_FETCHED_CONTENT, item);
            })).then(() => {
                commit(MUTATIONS.SET_USER, {
                    uuid: user_uuid,
                    user: {
                        comments: response.content.map(({ content_id }) => content_id),
                        total_comments: response.total_hits,
                    },
                });
            });
        });
    },

    [ACTIONS.FETCH_USER_BY_UUID]({ getters, dispatch, commit }, {
        uuid, site_uuid, origin, author_host_section_uuid,
    }) {
        // If its a socially ingested user, no need to fetch it
        if (isSociallyIngestedContent({ origin })) {
            return Promise.resolve(getters[GETTERS.USER_BY_ORIGIN]({
                actor_uuid: uuid,
                origin,
            }));
        }
        const user = getters[GETTERS.USER_BY_UUID](uuid);
        const default_user = getDefaultUser(getters[GETTERS.GET_TRANSLATION]('moderation.users.unassigned'), USER_LOAD_STATUSES.PENDING);
        // If the user has been fetched for the site already, just return it
        if (user) {
            // if site uuid is set on the user, then its been fetched for the site and never needs to be fetched again
            if (user.site_uuid !== ZERO_UUID) {
                return Promise.resolve(user);
            }
            // A repeat attempt — including a moderator retrying a failed read — drops any previous
            // failure so the row shows the neutral placeholder while this read is in flight.
            commit(MUTATIONS.SET_USER, {
                uuid,
                user: { load_status: USER_LOAD_STATUSES.PENDING },
            });
        }
        // If the user has not been fetched at all
        // (The user could have been fetched, but not for the specific site. In this case, we still need to fetch again and set it)
        else {
            commit(MUTATIONS.SET_USER, {
                uuid,
                user: default_user,
            });
        }

        // Record why the read failed instead of silently passing off the placeholder as the author.
        // site_uuid is deliberately left unset, so the next queue load (or an explicit retry) tries again.
        const registerFailure = (error) => {
            const load_status = classifyUserLoadFailure(error);
            commit(MUTATIONS.SET_USER, {
                uuid,
                user: { load_status },
            });
            return {
                ...default_user,
                load_status,
            };
        };

        const registerFromSection = (sectionUuid) => fetchUserByUuidAndSection(uuid, sectionUuid)
            .then((fetched) => fetchBanHistory(uuid, sectionLineage(getters[GETTERS.ALL_SITES], sectionUuid))
                .then((ban_history) =>
                    dispatch(ACTIONS.REGISTER_USER, {
                        user: {
                            ...fetched,
                            site_uuid,
                            ban_history,
                        },
                        isV2: false,
                    })));

        try {
            // First try to fetch the user for the site, if that fails, fall back to the author_host_section (syndication) when present
            return registerFromSection(site_uuid)
                .catch((error) => {
                    if (!author_host_section_uuid) {
                        return registerFailure(error);
                    }
                    return registerFromSection(author_host_section_uuid)
                        // A transient failure on either read outranks a 404 on the other: the author may
                        // well be readable once the failing section recovers, so it stays retryable.
                        .catch((fallbackError) => registerFailure(isTransientUserLoadFailure(error) ? error : fallbackError));
                })
                .catch(registerFailure);
        }
        // If anything goes wrong, then just return the placeholder instead of breaking the entire queue
        catch (e) {
            return Promise.resolve(registerFailure(e));
        }
    },

    [ACTIONS.FETCH_USER_BY_UUID_WITHOUT_SITE]({ getters, dispatch, commit }, { uuid }) {
        const user = getters[GETTERS.USER_BY_UUID](uuid);
        if (user) {
            return Promise.resolve(user);
        }

        const default_user = getDefaultUser(getters[GETTERS.GET_TRANSLATION]('moderation.users.unassigned'));
        commit(MUTATIONS.SET_USER, {
            uuid,
            user: default_user,
        });
        return fetchUserByUuidWithoutSite(uuid)
            .then((user) => dispatch(ACTIONS.REGISTER_USER, { user }))
            .catch(() => Promise.resolve(default_user));
    },
};
